// Persistent app-level banner (beneath the top nav, beside the Travel Rule
// banners) shown to a logged-in customer with an open adhoc re-KYC request.
// Session mode only: gated on the ADHOC_KYC_AUNZ flag and `GET
// /adhoc-kyc/me/context`, which answers `{ valid: false }` (never 401) when
// nothing is pending. Hidden on /adhocKyc itself.

import { Box, Typography } from "@mui/material";
import { VerifiedUserOutlined } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { useFeatureFlag } from "src/utils/hooks/useFeatureFlag";
import {
  ADHOC_KYC_FLAG,
  ADHOC_KYC_PATH,
  useGetAdhocKycContextQuery,
} from "src/store/features/api/adhocKyc";

export const AdhocKycBanner = (): JSX.Element | null => {
  const history = useHistory();
  const location = useLocation();
  const { enabled } = useFeatureFlag(ADHOC_KYC_FLAG);
  const { data } = useGetAdhocKycContextQuery({}, { skip: !enabled });

  if (location.pathname.startsWith(ADHOC_KYC_PATH)) return null;
  if (!data?.valid) return null;
  // A finished row still shows while it offers an in-flow retry: the customer
  // who left the final screen on "Not now" has no other way back to it.
  const retry = Boolean(data.retry);
  if (data.status === "COMPLETE" && !retry) return null;

  // Navy/mint so it reads as a routine account task, distinct from the
  // amber "deposit needs input" and grey/green Travel Rule banners.
  const palette = {
    bg: "#EAFBF4",
    border: "#0BEC9F",
    title: "#002035",
    body: "#1F2123",
    btn: "#002035",
  };

  return (
    <Box
      role="status"
      data-testid="adhoc-kyc-banner"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 1.25,
        px: 3,
        backgroundColor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        // Same left offset as the other banners so none sits under the
        // persistent 220px side drawer on desktop.
        ml: { xs: 0, md: "220px" },
      }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <VerifiedUserOutlined sx={{ fontSize: 22, color: palette.btn }} />
        <Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: palette.title }}>
            {retry ? "One more identity check is needed" : "Please re-verify your identity"}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: palette.body }}>
            {retry
              ? "The document issuer could not match some of your details. Please check and resubmit them."
              : "A quick identity check is due on your account. It takes about two minutes."}
          </Typography>
        </Box>
      </Box>
      <Box
        component="button"
        onClick={() => history.push(ADHOC_KYC_PATH)}
        data-testid="adhoc-kyc-banner-cta"
        sx={{
          backgroundColor: palette.btn,
          color: "#FFFFFF",
          border: "none",
          borderRadius: "999px",
          padding: "6px 14px",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}>
        {retry ? "Check and resubmit" : "Start now"}
      </Box>
    </Box>
  );
};
